import React, { useState } from 'react';
import { Wallet, Search, CreditCard, Zap, Loader2, ChevronRight, CheckCircle2 } from 'lucide-react';
import { usePlatform } from '../../context/PlatformContext';
import { platformAPI } from '../../lib/platformApi';
import { toast } from 'sonner';

export default function PlatformWallets() {
  const { wallets, walletsTotal, loaded, refresh, updateWallet } = usePlatform();
  const [search, setSearch] = useState('');
  const [activatingId, setActivatingId] = useState(null);
  const [bulkActivating, setBulkActivating] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletDetail, setWalletDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const filtered = wallets.filter(w =>
    w.name?.toLowerCase().includes(search.toLowerCase()) ||
    w.user_id?.toLowerCase().includes(search.toLowerCase()) ||
    w.virtual_account?.number?.includes(search) ||
    w.email?.toLowerCase().includes(search.toLowerCase())
  );

  const withVA = wallets.filter(w => w.virtual_account?.has_va).length;
  const withoutVA = wallets.filter(w => !w.virtual_account?.has_va).length;

  const handleActivateVA = async (walletId, e) => {
    e?.stopPropagation();
    setActivatingId(walletId);
    try {
      const res = await platformAPI.activateVA(walletId);
      if (res.data?.success) {
        toast.success(`VA activated for ${res.data.name}`);
        updateWallet(walletId, {
          virtual_account: { number: res.data.virtual_account.number, bank: res.data.virtual_account.bank || 'Assets MFB', has_va: true }
        });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to activate VA';
      if (err.response?.status === 409) {
        toast.info(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setActivatingId(null);
    }
  };

  const handleBulkActivate = async () => {
    if (withoutVA === 0) {
      toast.info('All wallets already have virtual accounts');
      return;
    }
    setBulkActivating(true);
    try {
      const res = await platformAPI.bulkActivateVA();
      const d = res.data;
      if (d.activated > 0) {
        toast.success(`Activated ${d.activated} virtual accounts`);
        await refresh();
      }
      if (d.failed > 0) {
        toast.error(`${d.failed} activations failed`);
      }
      if (d.activated === 0 && d.failed === 0) {
        toast.info(d.message || 'All wallets already have VAs');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk activation failed');
    } finally {
      setBulkActivating(false);
    }
  };

  const openDetail = async (wallet) => {
    setSelectedWallet(wallet);
    setLoadingDetail(true);
    try {
      const res = await platformAPI.getWalletDetail(wallet.wallet_id);
      setWalletDetail(res.data);
    } catch {
      toast.error('Failed to load wallet details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setSelectedWallet(null);
    setWalletDetail(null);
  };

  return (
    <div data-testid="platform-wallets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Wallets</h1>
          <p className="text-gray-500 mt-1">{walletsTotal} total &middot; {withVA} with VA &middot; {withoutVA} without VA</p>
        </div>
        {withoutVA > 0 && (
          <button
            onClick={handleBulkActivate}
            disabled={bulkActivating}
            data-testid="bulk-activate-va-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#E8678B] text-white text-sm font-medium rounded-lg hover:bg-[#d4557a] transition-colors disabled:opacity-50"
          >
            {bulkActivating ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            {bulkActivating ? 'Activating...' : `Activate All VAs (${withoutVA})`}
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          data-testid="wallets-search-input"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
          placeholder="Search by name, user ID, email, or account number..." />
      </div>

      {!loaded ? (
        <div className="text-center py-12 text-gray-400">Loading wallets...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-testid="wallets-empty-state">
          <Wallet size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">{search ? 'No wallets match your search' : 'No wallets yet'}</p>
          <p className="text-gray-400 text-xs mt-1">Create your first wallet via the API</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="wallets-list">
          {/* Header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-3">User</div>
            <div className="col-span-2">Virtual Account</div>
            <div className="col-span-2 text-right">Balance</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>

          <div className="divide-y divide-gray-100">
            {filtered.map(w => {
              const hasVA = w.virtual_account?.has_va;
              const isActivating = activatingId === w.wallet_id;

              return (
                <div key={w.wallet_id} className="grid grid-cols-12 gap-4 px-5 py-3.5 items-center hover:bg-gray-50/60 cursor-pointer transition-colors"
                  data-testid={`wallet-row-${w.wallet_id}`}
                  onClick={() => openDetail(w)}
                >
                  {/* User */}
                  <div className="col-span-12 sm:col-span-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center shrink-0">
                      <Wallet size={18} className="text-[#E8678B]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{w.name}</p>
                      <p className="text-xs text-gray-500 truncate">{w.user_id} {w.email ? `· ${w.email}` : ''}</p>
                    </div>
                  </div>

                  {/* VA */}
                  <div className="hidden sm:block col-span-2">
                    {hasVA ? (
                      <div>
                        <div className="flex items-center gap-1.5">
                          <CreditCard size={12} className="text-green-600" />
                          <span className="text-xs font-medium text-green-700">{w.virtual_account.number}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{w.virtual_account.bank}</p>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No VA</span>
                    )}
                  </div>

                  {/* Balance */}
                  <div className="hidden sm:block col-span-2 text-right">
                    <p className="text-sm font-semibold text-gray-900">₦{Number(w.balance).toLocaleString()}</p>
                    {w.available_balance !== w.balance && (
                      <p className="text-xs text-gray-400">avail: ₦{Number(w.available_balance).toLocaleString()}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="hidden sm:flex col-span-2 items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${w.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {w.status}
                    </span>
                    {w.is_sandbox && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700">sandbox</span>}
                  </div>

                  {/* Actions */}
                  <div className="hidden sm:flex col-span-3 items-center justify-end gap-2">
                    {!hasVA && (
                      <button
                        onClick={(e) => handleActivateVA(w.wallet_id, e)}
                        disabled={isActivating}
                        data-testid={`activate-va-${w.wallet_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[#E8678B] text-[#E8678B] hover:bg-pink-50 transition-colors disabled:opacity-50"
                      >
                        {isActivating ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                        {isActivating ? 'Activating...' : 'Activate VA'}
                      </button>
                    )}
                    {hasVA && <CheckCircle2 size={16} className="text-green-500" />}
                    <ChevronRight size={16} className="text-gray-400" />
                  </div>

                  {/* Mobile bottom row */}
                  <div className="col-span-12 sm:hidden flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${w.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {w.status}
                      </span>
                      {hasVA
                        ? <span className="text-xs text-green-600 flex items-center gap-1"><CreditCard size={10} /> {w.virtual_account.number}</span>
                        : <span className="text-xs text-gray-400 italic">No VA</span>
                      }
                    </div>
                    <p className="text-sm font-semibold text-gray-900">₦{Number(w.balance).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-500">Showing {filtered.length} of {walletsTotal} wallets</p>
          </div>
        </div>
      )}

      {/* Wallet Detail Slide-over */}
      {selectedWallet && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="wallet-detail-panel">
          <div className="absolute inset-0 bg-black/30" onClick={closeDetail} />
          <div className="relative w-full max-w-lg bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Wallet Details</h2>
              <button onClick={closeDetail} className="text-gray-400 hover:text-gray-600 text-sm" data-testid="close-wallet-detail">Close</button>
            </div>

            {loadingDetail ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={24} className="animate-spin text-[#E8678B]" />
              </div>
            ) : walletDetail ? (
              <div className="p-6 space-y-6">
                {/* User info */}
                <div>
                  <p className="text-xl font-bold text-gray-900">{walletDetail.wallet?.name}</p>
                  <p className="text-sm text-gray-500">{walletDetail.wallet?.user_id} &middot; {walletDetail.wallet?.email}</p>
                </div>

                {/* Balance card */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-5 text-white">
                  <p className="text-xs text-gray-400 mb-1">Balance</p>
                  <p className="text-2xl font-bold">₦{Number(walletDetail.wallet?.balance || 0).toLocaleString()}</p>
                  <div className="flex gap-6 mt-3 text-xs text-gray-400">
                    <span>Available: ₦{Number(walletDetail.wallet?.available_balance || 0).toLocaleString()}</span>
                    <span>Locked: ₦{Number(walletDetail.wallet?.locked_balance || 0).toLocaleString()}</span>
                  </div>
                </div>

                {/* VA info */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Virtual Account</p>
                  {walletDetail.wallet?.virtual_account?.has_va ? (
                    <div className="space-y-1">
                      <p className="text-sm text-gray-900 font-medium">{walletDetail.wallet.virtual_account.number}</p>
                      <p className="text-xs text-gray-500">{walletDetail.wallet.virtual_account.bank}</p>
                      {walletDetail.wallet.virtual_account.reference && (
                        <p className="text-xs text-gray-400 font-mono">{walletDetail.wallet.virtual_account.reference}</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-500">No virtual account assigned</p>
                      <button
                        onClick={() => handleActivateVA(walletDetail.wallet.wallet_id)}
                        disabled={activatingId === walletDetail.wallet?.wallet_id}
                        data-testid="detail-activate-va-btn"
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#E8678B] text-white hover:bg-[#d4557a] disabled:opacity-50"
                      >
                        {activatingId === walletDetail.wallet?.wallet_id ? 'Activating...' : 'Activate VA'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Volume summary */}
                {walletDetail.summary && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-green-50 rounded-xl p-4">
                      <p className="text-xs text-green-600 mb-1">Total Credits</p>
                      <p className="text-lg font-bold text-green-700">₦{Number(walletDetail.summary.total_credits_ngn || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-4">
                      <p className="text-xs text-orange-600 mb-1">Total Debits</p>
                      <p className="text-lg font-bold text-orange-700">₦{Number(walletDetail.summary.total_debits_ngn || 0).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {/* Recent transactions */}
                {walletDetail.recent_transactions?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">Recent Transactions</p>
                    <div className="space-y-2">
                      {walletDetail.recent_transactions.map(txn => (
                        <div key={txn.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100" data-testid={`detail-txn-${txn.id}`}>
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${txn.type === 'credit' ? 'bg-green-50' : 'bg-orange-50'}`}>
                              {txn.type === 'credit'
                                ? <span className="text-green-600 text-xs font-bold">+</span>
                                : <span className="text-orange-600 text-xs font-bold">-</span>}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-900">{txn.category?.replace('_', ' ') || txn.type}</p>
                              <p className="text-[10px] text-gray-400 font-mono">{txn.reference}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-gray-900'}`}>
                              {txn.type === 'credit' ? '+' : '-'}₦{Number(txn.amount).toLocaleString()}
                            </p>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${txn.status === 'completed' ? 'bg-green-50 text-green-700' : txn.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>
                              {txn.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
